import { useQuery } from "@apollo/client/react";

import React, { Suspense, useState, useEffect } from 'react'
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
} from 'react-router'
import {
  StyledEngineProvider,
} from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { AnimatePresence, motion } from 'framer-motion'
import { ColorModeProvider } from '@/theme/ColorModeProvider'
import AppThemeProvider from '@/theme/AppThemeProvider'
import SuspenseLoading from './SuspenseLoading'
import { useTour, FeedbackTour } from '@/housefolios-components/Tours'
import { PublicSidebar } from '@/layout-blueprints'
import PublicPropertyDashboardPage from '@/housefolios-pages/PublicPropertyDashboard'
import PublicMarketplacePage from '@/housefolios-pages/PublicMarketplace'
import PublicTeamPage from '@/housefolios-pages/PublicTeam'
import PublicHomePage from '@/housefolios-pages/PublicHome'
import PublicEducationPage from '@/housefolios-pages/PublicEducation'
import PublicTurnkeyPage from '@/housefolios-pages/PublicTurnkey'
import PublicCitiesPage from '@/housefolios-pages/PublicCities'
// Centralized lodash imports - much cleaner!
import { head } from '@/utils/lodash'
import { pageTransition, pageVariants } from '@/utils/animation'
import { GET_ORG_FROM_MARKETPLACE } from '@/housefolios-components/PublicProperty/queries'
import PdfContainer from '../PropertyContainer/Proforma/pdfContainer'
import ProformaRedirect from './ProformaRedirect'

function SubDomain({ subdomain  }) {
  const {
    openFeedbackTour,
    setOpenAnalyzeAreaTour,
    setOpenAnalyzeCompsTour,
    setOpenChooseStrategyTour,
    setOpenFeedbackTour,
    setOpenTakeActionTour,
    setOpenTour,
    setOpenVerifyFinancialsTour,
  } = useTour()
  const navigate = useNavigate()
  const { loading, error, data } = useQuery(
    GET_ORG_FROM_MARKETPLACE,
    {
      variables: {
        url: `${subdomain}.housefolios.com`,
      },
    },
  )
  if (loading) return <SuspenseLoading />
  if (error) return <p>Error: {error.message}</p>

  const hostname = import.meta.env.VITE_HOSTNAME
  let urlParams = ''

  if (
    hostname === 'localhost' ||
    hostname === 'dev-admin.housefolios.com'
  ) {
    urlParams = `?subdomain=${subdomain}`
  }

  const { getOrgFromMarketplace = {} } = data || {}
  return (
    <StyledEngineProvider injectFirst>
      <ColorModeProvider>
        <AppThemeProvider>
          <CssBaseline />
          <AnimatePresence>
            <Suspense fallback={<SuspenseLoading />}>
              <Routes>
              <Route
                element={
                  <PublicSidebar
                    marketplace={
                      getOrgFromMarketplace
                        ? head(getOrgFromMarketplace.marketPlaces)
                        : null
                    }
                    orgName={
                      getOrgFromMarketplace
                        ? getOrgFromMarketplace.name
                        : ''
                    }
                  />
                }
              >
                <Route
                  path="/public-property-details/:id/:view"
                  element={
                    <PublicPropertyDashboardPage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                      setIsOpen={setOpenTour}
                      setIsOpenVerifyFinancialsTour={
                        setOpenVerifyFinancialsTour
                      }
                      setIsOpenAnalyzeCompsTour={
                        setOpenAnalyzeCompsTour
                      }
                      setIsOpenChooseStrategyTour={
                        setOpenChooseStrategyTour
                      }
                      setIsOpenAnalyzeAreaTour={
                        setOpenAnalyzeAreaTour
                      }
                      setIsOpenTakeActionTour={setOpenTakeActionTour}
                    />
                  }
                />

                <Route
                  path="/soldproperties"
                  element={
                    <PublicMarketplacePage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                      sold
                    />
                  }
                />
                <Route
                  path="/properties"
                  element={
                    <PublicMarketplacePage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />
                <Route
                  path="/team"
                  element={
                    <PublicTeamPage
                      orgId={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace._id
                          : ''
                      }
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />

                <Route
                  path="/education"
                  element={
                    <PublicEducationPage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />

                <Route
                  path="/turnkey"
                  element={
                    <PublicTurnkeyPage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />

                <Route
                  path="/cities"
                  element={
                    <PublicCitiesPage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />

                <Route
                  path="/property/:id/proforma/:type"
                  element={<PdfContainer public={true} />}
                />

                <Route
                  path="/property-details/:id/proforma/:type"
                  element={<ProformaRedirect isLoggedIn={false} />}
                />

                <Route
                  path="/"
                  element={
                    <PublicHomePage
                      orgName={
                        getOrgFromMarketplace
                          ? getOrgFromMarketplace.name
                          : ''
                      }
                      marketplace={
                        getOrgFromMarketplace
                          ? head(getOrgFromMarketplace.marketPlaces)
                          : null
                      }
                    />
                  }
                />

                <Route
                  path="/*"
                  element={<Navigate to={`/${urlParams}`} />}
                />
              </Route>
            </Routes>
          </Suspense>
        </AnimatePresence>
        <FeedbackTour
          isOpenFeedbackTour={openFeedbackTour}
          closeFeedbackTour={() => setOpenFeedbackTour(false)}
        />
        </AppThemeProvider>
      </ColorModeProvider>
    </StyledEngineProvider>
  )
}

export default SubDomain
